import { Fragment, type ReactNode } from 'react'

/**
 * Minimal, safe Markdown for agent replies: paragraphs, bullet and numbered
 * lists, headings (rendered bold), **bold**, *italic*, `code` and https links.
 * Everything is built as React nodes; no HTML is ever injected.
 */
export function MarkdownLite({ text }: { text: string }) {
  const blocks = toBlocks(text)
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => {
        if (b.kind === 'ul') return <ul key={i} className="list-disc space-y-0.5 pl-5">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>
        if (b.kind === 'ol') return <ol key={i} className="list-decimal space-y-0.5 pl-5">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ol>
        if (b.kind === 'heading') return <p key={i} className="font-semibold">{inline(b.text)}</p>
        return <p key={i}>{b.lines.map((l, j) => <Fragment key={j}>{j > 0 && <br />}{inline(l)}</Fragment>)}</p>
      })}
    </div>
  )
}

type Block = { kind: 'p'; lines: string[] } | { kind: 'ul'; items: string[] } | { kind: 'ol'; items: string[] } | { kind: 'heading'; text: string }

function toBlocks(text: string): Block[] {
  const blocks: Block[] = []
  let current: Block | null = null
  const flush = () => { if (current) blocks.push(current); current = null }
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd()
    if (!line.trim()) { flush(); continue }
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line)
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    const h = /^\s*#{1,6}\s+(.*)$/.exec(line)
    if (ul) { if (current?.kind !== 'ul') { flush(); current = { kind: 'ul', items: [] } } ; (current as { items: string[] }).items.push(ul[1]); continue }
    if (ol) { if (current?.kind !== 'ol') { flush(); current = { kind: 'ol', items: [] } } ; (current as { items: string[] }).items.push(ol[1]); continue }
    if (h) { flush(); blocks.push({ kind: 'heading', text: h[1] }); continue }
    if (current?.kind !== 'p') { flush(); current = { kind: 'p', lines: [] } }
    ;(current as { lines: string[] }).lines.push(line.trim())
  }
  flush()
  return blocks
}

const INLINE = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let k = 0
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0
    if (start > last) out.push(text.slice(last, start))
    const tok = m[0]
    if (tok.startsWith('**')) out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>)
    else if (tok.startsWith('`')) out.push(<code key={k++} className="rounded px-1 text-[0.9em]" style={{ backgroundColor: 'var(--color-gray-100)' }}>{tok.slice(1, -1)}</code>)
    else if (tok.startsWith('[')) {
      const link = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/.exec(tok)!
      out.push(<a key={k++} href={link[2]} target="_blank" rel="noreferrer noopener" className="underline" style={{ color: 'var(--color-text-link)' }}>{link[1]}</a>)
    } else out.push(<em key={k++}>{tok.slice(1, -1)}</em>)
    last = start + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}
